import React, { useState, useEffect } from 'react';
import {
  Table,
  Tag,
  Button,
  Space,
  Badge,
  Descriptions,
  Card,
  Collapse,
  Typography,
  message,
  Tooltip,
} from 'antd';
import {
  ReloadOutlined,
  StopOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { KrybotService } from '../../../services/api/krybotService';
import type { KrybotAgentRun, RunStatus } from '../../../types/krybot';
import styles from './AgentManagement.module.css';

const { Panel } = Collapse;
const { Text, Paragraph } = Typography;

interface AgentRunMonitorProps {
  agentId: string;
}

export const AgentRunMonitor: React.FC<AgentRunMonitorProps> = () => {
  const [runs, setRuns] = useState<KrybotAgentRun[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedRun, setSelectedRun] = useState<KrybotAgentRun | null>(null);

  useEffect(() => {
    loadRuns();
    // Poll for updates every 5 seconds
    const interval = setInterval(loadRuns, 5000);
    return () => clearInterval(interval);
  }, []);

  const loadRuns = async () => {
    try {
      setLoading(true);
      const allRuns = await KrybotService.listAgentRuns();
      // Fetch full details for recent runs
      const runDetails = await Promise.all(
        allRuns.slice(0, 20).map((run) =>
          KrybotService.getAgentRun(run.id).catch(() => null)
        )
      );
      setRuns(runDetails.filter((r): r is KrybotAgentRun => r !== null));
    } catch (err: any) {
      message.error(`Failed to load runs: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (runId: string) => {
    try {
      await KrybotService.cancelAgentRun(runId);
      message.success('Run cancelled');
      loadRuns();
    } catch (err: any) {
      message.error(`Failed to cancel run: ${err.message}`);
    }
  };

  const getStatusColor = (status: RunStatus): string => {
    const colors: Record<RunStatus, string> = {
      queued: 'default',
      in_progress: 'processing',
      completed: 'success',
      requires_approval: 'warning',
      failed: 'error',
      cancelled: 'default',
    };
    return colors[status] || 'default';
  };

  const getStatusIcon = (status: RunStatus) => {
    switch (status) {
      case 'completed':
        return <CheckCircleOutlined />;
      case 'failed':
        return <CloseCircleOutlined />;
      case 'in_progress':
        return <ClockCircleOutlined />;
      case 'requires_approval':
        return <ExclamationCircleOutlined />;
      default:
        return null;
    }
  };

  const columns = [
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: RunStatus) => (
        <Badge
          status={getStatusColor(status) as any}
          text={
            <Space>
              {getStatusIcon(status)}
              {status.replace('_', ' ').toUpperCase()}
            </Space>
          }
        />
      ),
    },
    {
      title: 'Agent',
      key: 'agent',
      render: (_: unknown, record: KrybotAgentRun) => (
        <Text>{record.agent?.display_name || record.agent?.name || 'Unknown'}</Text>
      ),
    },
    {
      title: 'Scope',
      dataIndex: 'artifact_scope',
      key: 'artifact_scope',
      render: (scope: string | undefined) => (
        <Tag>{scope ? scope.toUpperCase() : 'NONE'}</Tag>
      ),
    },
    {
      title: 'Orch.',
      key: 'orchestration_id',
      width: 90,
      ellipsis: true,
      render: (_: unknown, record: KrybotAgentRun) =>
        record.orchestration_id ? (
          <Tooltip title={record.orchestration_id}>
            <Text copyable={{ text: record.orchestration_id }} ellipsis>
              {record.orchestration_id.slice(0, 8)}…
            </Text>
          </Tooltip>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: 'Started',
      dataIndex: 'started_at',
      key: 'started_at',
      render: (date: string | undefined) =>
        date ? new Date(date).toLocaleString() : 'N/A',
    },
    {
      title: 'Completed',
      dataIndex: 'completed_at',
      key: 'completed_at',
      render: (date: string | undefined) =>
        date ? new Date(date).toLocaleString() : 'N/A',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, record: KrybotAgentRun) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => setSelectedRun(record)}
            />
          </Tooltip>
          {(record.status === 'queued' || record.status === 'in_progress') && (
            <Tooltip title="Cancel Run">
              <Button
                type="text"
                danger
                icon={<StopOutlined />}
                onClick={() => handleCancel(record.id)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="agent-run-monitor">
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
        <Text strong>Agent Runs</Text>
        <Button icon={<ReloadOutlined />} onClick={loadRuns}>
          Refresh
        </Button>
      </Space>

      <Table
        columns={columns}
        dataSource={runs}
        loading={loading}
        rowKey="id"
        pagination={{ pageSize: 10 }}
        onRow={(record) => ({
          onClick: () => setSelectedRun(record),
          style: { cursor: 'pointer' },
        })}
      />

      {selectedRun && (
        <Card
          title={`Run Details: ${selectedRun.id.slice(0, 8)}...`}
          extra={
            <Button onClick={() => setSelectedRun(null)}>Close</Button>
          }
          style={{ marginTop: 16 }}
        >
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="Status">
              <Badge
                status={getStatusColor(selectedRun.status) as any}
                text={selectedRun.status.replace('_', ' ').toUpperCase()}
              />
            </Descriptions.Item>
            <Descriptions.Item label="Agent">
              {selectedRun.agent?.display_name || selectedRun.agent?.name || 'Unknown'}
            </Descriptions.Item>
            <Descriptions.Item label="Scope">
              <Tag>{selectedRun.artifact_scope?.toUpperCase() || 'NONE'}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Started">
              {selectedRun.started_at
                ? new Date(selectedRun.started_at).toLocaleString()
                : 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Completed">
              {selectedRun.completed_at
                ? new Date(selectedRun.completed_at).toLocaleString()
                : 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Prime Artifact ID">
              {selectedRun.prime_artifact_id || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Company Artifact ID">
              {selectedRun.company_artifact_id || 'N/A'}
            </Descriptions.Item>
          </Descriptions>

          {selectedRun.input_payload && (
            <div style={{ marginTop: 16 }}>
              <Text strong>Input Payload:</Text>
              <Card size="small" style={{ marginTop: 8 }}>
                <pre className="json-editor">
                  {JSON.stringify(selectedRun.input_payload, null, 2)}
                </pre>
              </Card>
            </div>
          )}

          {selectedRun.output_payload && (
            <div style={{ marginTop: 16 }}>
              <Text strong>Output Payload:</Text>
              <Card size="small" style={{ marginTop: 8 }}>
                <pre className="json-editor">
                  {JSON.stringify(selectedRun.output_payload, null, 2)}
                </pre>
              </Card>
            </div>
          )}

          {selectedRun.error_detail && (
            <div style={{ marginTop: 16 }}>
              <Text strong type="danger">Error Details:</Text>
              <Card size="small" style={{ marginTop: 8 }}>
                <pre className="json-editor">
                  {JSON.stringify(selectedRun.error_detail, null, 2)}
                </pre>
              </Card>
            </div>
          )}

          {selectedRun.actions && selectedRun.actions.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <Text strong>Actions:</Text>
              <Collapse style={{ marginTop: 8 }}>
                {selectedRun.actions.map((action, idx) => (
                  <Panel
                    key={action.id}
                    header={`${action.action_type} - ${action.status} (Order: ${action.sequence_order || idx})`}
                  >
                    <Descriptions column={1} size="small">
                      <Descriptions.Item label="Type">{action.action_type}</Descriptions.Item>
                      <Descriptions.Item label="Status">{action.status}</Descriptions.Item>
                      {action.request_payload && (
                        <Descriptions.Item label="Request">
                          <pre className="json-editor">
                            {JSON.stringify(action.request_payload, null, 2)}
                          </pre>
                        </Descriptions.Item>
                      )}
                      {action.response_payload && (
                        <Descriptions.Item label="Response">
                          <pre className="json-editor">
                            {JSON.stringify(action.response_payload, null, 2)}
                          </pre>
                        </Descriptions.Item>
                      )}
                    </Descriptions>
                  </Panel>
                ))}
              </Collapse>
            </div>
          )}

          {selectedRun.approval && (
            <div style={{ marginTop: 16 }}>
              <Text strong>Pending Approval:</Text>
              <Card size="small" style={{ marginTop: 8 }}>
                <Text>Approval ID: {selectedRun.approval.id}</Text>
                <br />
                <Text>Status: {selectedRun.approval.status}</Text>
              </Card>
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

